import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import DetailsBlock from '../components/tables/DetailsBlock';
import { DetailsValueBlock } from '../components/tables/DetailsValueBlock';
import { localizations } from '../localization/localization';
import DetailsContainer from '../components/tables/DetailsContainer';
import { renderEmailDetails, renderPersonDetails, renderPhoneDetails, renderCompanyDetails, renderWebsiteDetails, renderTruckProperties } from '../components/tables/DetailsHelpers';
import { toCommaDecimal } from '../utils/numberUtils';
import * as TruckStore from '../trucks/store/truckStore'
import { ApplicationState } from "../store";
import { connect } from 'react-redux';
import { FilterInfo, FilterMode } from '../common/models/CommonModels';
import moment from 'moment';
import { Theme, withStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import { momentToString } from '../utils/dateUtilities';
import { RouteComponentProps } from 'react-router';
import { push } from 'react-router-redux';
import IconButton from '@material-ui/core/IconButton';
import MapIcon from '@material-ui/icons/Map';
import Tooltip from '@material-ui/core/Tooltip';
import forwardImg from "../images/forward.svg";
import emailImg from "../images/email.svg";
import { RouteName } from '../routeNames';
import TruckEmailDialog from './forms/TruckEmailDialog';

const styles = (theme: Theme) => ({
    leftIcon: {
        marginRight: theme.spacing(1),
    },
});

interface TruckProps {
    trucks: TruckStore.TrucksState;
    identifier: string;
    goTo: (location: string) => void
}

interface TruckViewState {
    filters: FilterInfo;
}

interface TruckMatchProps {
    identifier: string;
}

type TruckViewProps = TruckProps & RouteComponentProps<TruckMatchProps> & { truckActions: typeof TruckStore.actionCreators };

class TruckView extends React.Component<TruckViewProps, TruckViewState> {

    constructor(props: TruckViewProps) {
        super(props);

        this.state = {
            filters: {
                filterMode: FilterMode.All,
                fromCities: [],
                fromDate: moment().startOf("day").subtract(365, "days"),
                properties: [],
                reportNumberSearch: this.props.match.params.identifier,
                reporter: [],
                toCities: [],
                toDate: moment().startOf("day").add(365, "days"),
            },
        }
    }

    public componentDidMount() {
        this.props.truckActions.requestFreeTrucks(this.state.filters, true);
    }

    public render() {
        const truck = this.props.trucks.data[0];
        const { truckActions, goTo, trucks } = this.props;
        const isEmailDialogOpen = trucks.isSendingEmail || trucks.isSendingEmailToReporter;

        function Buttons() {
            function goToMap() {
                truckActions.selectTruck(truck);
                goTo(RouteName.Map);
            }

            return(
            <Grid container direction="row" justify="flex-end">
                <Grid item>
                    <Tooltip title={localizations.actions.showOnMap}>
                        <IconButton style={{ height: 32, width: 32 }} onClick={() => goToMap()}><MapIcon color="primary" /></IconButton>
                    </Tooltip>
                </Grid>
                <Grid item style={{paddingLeft: 16}}>
                    <Tooltip title={localizations.actions.sendInfoByEmail}>
                        <IconButton style={{ height: 32, width: 32 }} onClick={() => truckActions.sendEmail(truck)}><img src={forwardImg} width='16' height='16' /></IconButton>
                    </Tooltip>
                </Grid>
                <Grid item style={{paddingLeft: 16}}>
                    <Tooltip title={localizations.actions.sendEmailToReporter}>
                        <IconButton style={{ height: 32, width: 32 }} onClick={() => truckActions.sendEmailToReporter(truck)}><img src={emailImg} width='16' height='16' /></IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
            )
        }

        return (
            truck ?
                (<>
                    <Grid item>
                        <DetailsContainer>
                            <DetailsBlock
                                title={localizations.trucks.truckDetailsHeader}
                                rightItems={<Buttons />}>
                                <DetailsValueBlock title={localizations.common.reportNumber}>
                                    <Typography>{truck.reportNumber}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.common.startTime}>
                                    <Typography>{momentToString(truck.availableFrom)}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.common.endTime}>
                                    <Typography>{momentToString(truck.availableTo)}</Typography>
                                </DetailsValueBlock>
                                <DetailsValueBlock title={localizations.common.pointOfDeparture}>
                                    <Typography>{truck.departureCity.name}</Typography>
                                </DetailsValueBlock>
                                {truck.destinationCity ? <DetailsValueBlock title={localizations.common.pointOfArrival}>
                                    <Typography>{truck.destinationCity?.name}</Typography>
                                </DetailsValueBlock> : null}
                                <DetailsValueBlock title={localizations.common.properties}>
                                    {renderTruckProperties(truck.properties)}
                                </DetailsValueBlock>
                                {truck.plateNumber ? <DetailsValueBlock title={localizations.trucks.licensePlateNumber}>
                                    <Typography>{truck.plateNumber}</Typography>
                                </DetailsValueBlock> : null}
                                {truck.contractPrice ? <DetailsValueBlock title={localizations.common.contractPriceCol}>
                                    <Typography>{`${toCommaDecimal(truck.contractPrice)} €/km`}</Typography>
                                </DetailsValueBlock> : null}
                                {truck.details ? <DetailsValueBlock title={localizations.common.details}>
                                    <Typography>{truck.details}</Typography>
                                </DetailsValueBlock> : null}
                            </DetailsBlock>
                            {truck.company ? <DetailsBlock
                                title={localizations.trucks.companyDetailsHeader}
                            >
                                {renderPersonDetails(truck.coordinator, localizations.common.reporter)}
                                {renderCompanyDetails(truck.company)}
                                {truck.company.phoneContacts.map((item, index) => <React.Fragment key={index}>
                                    {renderPhoneDetails(item, index + 1)}
                                </React.Fragment>)}
                                {truck.company.emailContacts.map((item, index) => <React.Fragment key={index}>
                                    {renderEmailDetails(item, index + 1)}
                                </React.Fragment>)}
                                {renderWebsiteDetails(truck.company.website)}
                            </DetailsBlock> : null}
                        </DetailsContainer>
                    </Grid>
                    {isEmailDialogOpen && (
                        <TruckEmailDialog
                            isOpen={isEmailDialogOpen}
                            onSubmit={trucks.isSendingEmail ? truckActions.sendEmailSubmitted : truckActions.sendEmailToReporterSubmitted}
                            onCancel={trucks.isSendingEmail ? truckActions.sendEmailCancelled : truckActions.sendEmailToReporterCancelled}
                            toReporter={trucks.isSendingEmailToReporter}
                        />
                    )}
                </>)
                : null);
    }
}

const mapStateToProps = (state: ApplicationState) => {
    const { trucks } = state;
    return { trucks };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        truckActions: bindActionCreators(TruckStore.actionCreators, dispatch),
        goTo: (location: string) => dispatch(push(location)),
    }
}

export default withStyles(styles)(connect<any, any, any>(mapStateToProps, mapDispatchToProps)(TruckView));


// WEBPACK FOOTER //
// ./src/trucks/TruckView.tsx